import { useState } from "react";
import { Loader2, Link2, Code2, Hash, CalendarDays, Type } from "lucide-react";
import { followTitle, platformOf } from "@/lib/editionForm";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * The edition's own fields (Digital editions, 2026-10-19), shared by
 * the New edition form and the edition page:
 *   date → title (follows the date until the editor types one) →
 *   issue number (optional, becomes a WordPress tag) →
 *   full-screen link → embed code.
 *
 * `initial` = {edition_date, title, issue_number, view_url, embed_code}.
 * `onSubmit(values)` gets the same shape. `extra` renders under the
 * fields (the cover picker on the New edition form).
 */
export default function EditionForm({
  initial, settings, siteName, onSubmit, submitLabel, busy, extra, testId = "edition-form",
}) {
  const [v, setV] = useState(() => ({
    edition_date: initial?.edition_date || "",
    title: initial?.title || "",
    issue_number: initial?.issue_number || "",
    view_url: initial?.view_url || "",
    embed_code: initial?.embed_code || "",
  }));
  const template = settings?.edition_settings_effective?.title_template || "{date} Edition";
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  // Date and issue both feed the title pattern, so the title follows
  // either until the editor has typed their own.
  const setFollowing = (k, val) =>
    setV((p) => {
      const next = { ...p, [k]: val };
      next.title = followTitle({
        title: p.title,
        template,
        site: siteName,
        before: { date: p.edition_date, issue: p.issue_number },
        after: { date: next.edition_date, issue: next.issue_number },
      });
      return next;
    });

  const linkHost = platformOf(v.view_url);
  const inputCls =
    "w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]";

  return (
    <form
      data-testid={testId}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(v);
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor={`${testId}-date`} className="overline flex items-center gap-1.5 mb-1.5">
            <CalendarDays className="w-3 h-3" />{" "}{tr("Edition date")}</label>
          <input
            id={`${testId}-date`}
            data-testid={`${testId}-date`}
            type="date"
            required
            value={v.edition_date}
            onChange={(e) => setFollowing("edition_date", e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`${testId}-issue`} className="overline flex items-center gap-1.5 mb-1.5">
            <Hash className="w-3 h-3" />{" "}{trRich("Issue number <0>(optional, added as a tag)</0>", null, [<span className="normal-case tracking-normal text-[var(--ink-muted)]" />])}
          </label>
          <input
            id={`${testId}-issue`}
            data-testid={`${testId}-issue`}
            value={v.issue_number}
            onChange={(e) => setFollowing("issue_number", e.target.value)}
            placeholder="e.g. 3204"
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${testId}-title`} className="overline flex items-center gap-1.5 mb-1.5">
          <Type className="w-3 h-3" />{" "}{tr("Post title")}</label>
        <input
          id={`${testId}-title`}
          data-testid={`${testId}-title`}
          value={v.title}
          onChange={(e) => set("title", e.target.value)}
          placeholder={tr("Filled in from the date")}
          className="w-full px-3 py-2 text-lg font-serif-display border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
        />
      </div>

      <div>
        <label htmlFor={`${testId}-link`} className="overline flex items-center gap-1.5 mb-1.5">
          <Link2 className="w-3 h-3" />{" "}{tr("Digital edition link")}{linkHost && (
            <span className="ml-1 normal-case tracking-normal text-[11px] text-emerald-700">· {linkHost}</span>
          )}
        </label>
        <input
          id={`${testId}-link`}
          data-testid={`${testId}-link`}
          type="url"
          required
          value={v.view_url}
          onChange={(e) => set("view_url", e.target.value.trim())}
          placeholder="https://indd.adobe.com/view/…  or  https://www.flipsnack.com/…"
          className={inputCls}
        />
        <p className="mt-1 text-[11px] text-[var(--ink-muted)]">{tr("Goes first in the post, so readers can open the edition full screen.")}</p>
      </div>

      <div>
        <label htmlFor={`${testId}-embed`} className="overline flex items-center gap-1.5 mb-1.5">
          <Code2 className="w-3 h-3" />{" "}{tr("Embed code")}</label>
        <textarea
          id={`${testId}-embed`}
          data-testid={`${testId}-embed`}
          required
          rows={3}
          value={v.embed_code}
          onChange={(e) => set("embed_code", e.target.value)}
          placeholder={tr("<iframe src=\"https://indd.adobe.com/embed/…\" …></iframe>")}
          className={inputCls + " font-mono-display text-xs"}
        />
        <p className="mt-1 text-[11px] text-[var(--ink-muted)]">{tr("Adobe Publish Online: Share → Embed. Flipsnack: Share → Embed. Paste the whole <iframe> code; the viewer goes under the link.")}</p>
      </div>

      {extra}

      <div className="flex justify-end">
        <button
          type="submit"
          data-testid={`${testId}-submit`}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-60"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
